import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// --------------------------------------------------------------------------
// Auto-refresh access token on 401.
//
// Access tokens live 2h, refresh tokens live 7d (see backend server.py).
// Without this interceptor, an admin who leaves a tab open for >2h would
// silently 401 on the next save with no explanation. With it, axios catches
// the first 401, calls /auth/refresh once, and retries the original request.
// If the refresh also fails the user is genuinely logged out and the 401
// surfaces to the caller — where the UI shows "Session expired" status.
//
// We coalesce concurrent refresh attempts so a burst of parallel calls only
// triggers one refresh round-trip.
// --------------------------------------------------------------------------
let refreshPromise = null;

const refreshSession = () => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE}/auth/refresh`, null, { withCredentials: true })
      .finally(() => {
        // Always clear so the *next* 401 (later) can try again.
        // Tiny defer keeps in-flight retries from racing the clear.
        setTimeout(() => {
          refreshPromise = null;
        }, 0);
      });
  }
  return refreshPromise;
};

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Only auto-refresh on 401, and only for requests that haven't already been retried.
    // Never try to refresh the refresh/login endpoints themselves — that would loop.
    const isAuthEndpoint =
      original?.url &&
      (original.url.includes("/auth/refresh") ||
        original.url.includes("/auth/login") ||
        original.url.includes("/auth/logout"));

    if (status === 401 && !original?._retried && !isAuthEndpoint) {
      original._retried = true;
      try {
        await refreshSession();
        return api(original);
      } catch (e) {
        // Refresh failed — propagate the original 401 so the caller can show
        // "Session expired" to the user. We do not redirect here so individual
        // forms can decide how to surface the error.
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export const formatError = (detail) => {
  if (detail == null) return "Something went sideways. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};
